<template lang='pug'>
.hundo
  .min-weight(v-if="minimumWeightRemaining > 0")
    .font-size-xs {{ __('Required minimum weight remaining') }}
    b.lbs-away {{minimumWeightRemaining}}LB
  .remaining(v-else)
    template(v-if='nextTier')
      .font-size-xs(v-if='nextTier && nextTier.ruleWeight === tiers[0].ruleWeight')
        | {{ __('Pounds Away From HundoPricing') }}
      .font-size-xs(v-else) {{ __('Pounds Away From Better HundoPricing') }}
      b.lbs-away {{ nextTier.ruleWeight - weight }}LB
    .font-size-xs(v-else-if="weight") {{ __('Congratulations! You\'ve maxed out on HundoPricing.') }}
  .bar
    .progress(:style='{width: totalProgress * 100 + "%"}' :class="{ progressUnderMinWeight: !minimumWeightMet }" )
    .tier(
      v-for='tier in tiers'
      :style='{width: tier.ruleWeight / topTierWeight * 100 + "%"}'
    )
  .prices
    .base-price
      .lead {{ __('Total Price') }}:
      price(
        :show-excl-vat="true"
        :buyRequest="buyRequest"
        :showTotalPrice="false"
        theme="rhpa"
      )
    .hundo-price
      .lead
        img.hundo-icon(src='@/assets/hundo-pricing.svg' :alt="__('Hundo pricing')")
        Tooltip
          template(#tooltip)
            | {{ __('Discounted pricing is based on the total weight both selected and already added to cart. Adding more weights provides better discounts, which will update in your cart.') }}
      .price(v-if='discount') {{ rogue.currency(Math.max(0, itemPrice - discount), {euTaxClass: true}) }}
      .price(v-else) &mdash;
</template>

<script lang="ts" setup>
import { getHundoDiscount, type IHundoCalculationResult } from '@rogue/universal'
import { onMounted, ref, onServerPrefetch, watch, computed } from 'vue'
import { computePriceSync } from '@/js/helpers/price-utils'
import { listProductIds } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'

type propTypes = {
  product: Product
  buyRequest: BuyRequest
  minimumWeight?: number
}

const props = withDefaults(defineProps<propTypes>(), {
  minimumWeight: -1,
})

const listStore = useListStore()
const productStore = useProductStore()
const rogue = useRogue()
let loaded = false

const products = computed(() => productStore.products)

const hundoResult = ref<IHundoCalculationResult>({
  total               : 0,
  discountsPerGrouped : {},
  discountsPerItem    : {},
  rowWeightPerGrouped : {},
  matchTierPerGrouped : {},
})

const cart = computed(() => listStore.cart)

const tiers = computed(() => (props.product.hundoPricing || []).slice(0).sort((tierA, tierB) => tierA.ruleWeight - tierB.ruleWeight))

const topTierWeight = computed(() => tiers.value[tiers.value.length - 1]?.ruleWeight)

const weight = computed(() => hundoResult.value.rowWeightPerGrouped[props.product.productId] || 0)

const totalProgress = computed(() => {
  let progress = weight.value / topTierWeight.value
  if (progress > 1) progress = 1

  return progress
})

const minimumWeightRemaining = computed(() => props.minimumWeight - weight.value)

const minimumWeightMet = computed(() => minimumWeightRemaining.value <= 0)

const nextTier = computed(() => tiers.value.find(tier => tier.ruleWeight > weight.value))

// We want to find all cart items that are grouped products so they can be loaded and used to calculate the hundo discount
const cartItemsWithGroupedIds = computed(() => cart.value.items.filter(item => item.metadata?.groupedProductId))

const activeChildren = computed(() => {
  const tempId = Date.now()

  return (props.buyRequest?.children || [])
    .filter(br => br.qty > 0)
  // We need to make copies of these things so we can add listItemId
    .map(br => JSON.parse(JSON.stringify({
      ...br,
      // check if not grouped product, then do multipication
      qty        : props.buyRequest.type === 'group' ? br.qty : br.qty * props.buyRequest?.qty,
      _id        : `${tempId}-${br.productId}`,
      listItemId : `${tempId}-${br.productId}`,
    })))
})

const discount = computed(() => {
  /**
       * the hundoResult object will have results from *cart* items as well - so we need to find the discount
       * that was given for the specific products in this rhpa-hundo's buy request
       */
  let deduction = 0

  activeChildren.value.forEach(child => deduction += (hundoResult.value.discountsPerItem?.[child.listItemId] || 0))

  return deduction
})

const itemPrice = computed(() => computePriceSync(props.buyRequest, rogue.pinia) || 0)

// Update hundoResult with the new hundo discount whenever a child is incremented or decremented.
watch(activeChildren, () => {
  if (!loaded) {
    return
  }
  hundoResult.value = getHundoDiscount([...cartItemsWithGroupedIds.value, ...activeChildren.value], rogue)
}, {
  deep: true,
})

// Update hundoResult with the new hundo discount when items are added or removed from the cart.
watch(cart, () => {
  if (!loaded) {
    return
  }
  hundoResult.value = getHundoDiscount([...cartItemsWithGroupedIds.value, ...activeChildren.value], rogue)
}, { deep: true })

async function init() {
  const groupedId = props.buyRequest.metadata?.groupedProductId
  if (groupedId) {
    productStore.load(groupedId, rogue)
  }

  await listStore.init(rogue)
}

onServerPrefetch(async () => {
  await init()
})

async function loadCart() {
  // We need the grouped products (which have Hundo pricing) to be loaded - they might be in the cart, so load them here
  // Reload all simple products from the net
  const groupedProductIds = cartItemsWithGroupedIds.value
    .map(item => item.metadata?.groupedProductId)

  if (groupedProductIds.length) {
    await productStore.loadAll(groupedProductIds, rogue)
  }

  // Only check items that have a grouped product id (all hundo products have grouped ids) AND
  // where those grouped products are loaded AND have hundopricing
  const cartItemsWithHundo = cart.value.items
    .filter(i => i.metadata?.groupedProductId && rogue.getProductById(i.metadata?.groupedProductId)?.hundoPricing)

  hundoResult.value = getHundoDiscount(cartItemsWithHundo.concat(activeChildren.value), rogue)

  loaded = true
}

onMounted(() => {
  init().then(() => loadCart())
})
</script>

<style lang='scss' scoped>
.hundo {
  margin-bottom: $space-m;
}

.remaining {
  margin-bottom: $space-xxs;
}

.min-weight {
  margin-bottom: $space-xxs;
}

.lbs-away {
  font-size: $size-xl;
  display: block;
  margin-top: $space-xxs;
}

.bar {
  position: relative;
  height: $space-xxs;
  width: 100%;
  background-color: $color-gray-light;
  margin-bottom: $space-s;
}

.tier {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;

  &:not(:last-child) {
    border-right: $border-width-thin solid $color-black;
  }
}

.progress {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  background-color: $color-green;
  transition: width 200ms ease;
}

.progressUnderMinWeight {
  background-color: $color-red;
}

.prices {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.hundo-price {
  text-align: right;
}

.lead {
  font-size: $size-xs;
  line-height: $line-height-s;
  margin-bottom: $space-xxs;
  display: flex;
  align-items: center;
}

.price {
  font-size: $size-l;
  line-height: $line-height-s;
  font-weight: $weight-bold;
  font-family: $font-condensed;
}

.hundo-icon {
  width: 132px;
  height: 12px;
  margin-right: $space-xxxs;
}

:deep(.ttip .default-icon) {
  margin: -8px;
}
</style>
